import React, { useState, useEffect, lazy, Suspense } from 'react';
import { ResponsiveStyles } from './shared/ResponsiveStyles.jsx';
import { PortalLoader } from './shared/components.jsx';
import { CookieBanner } from './shared/CookieBanner.jsx';
import { UnifiedLogin } from './shared/UnifiedLogin.jsx';
import { setTokenRefreshCallback } from './shared/APIStore.js';
import { AuditLogger } from './shared/AuditLogger.js';
import { setViewAs, clearViewAs } from './shared/viewAs.js';
import { Encryption } from './shared/Security.js';
import { consumeReturnTicket } from './shared/returnTicket.js';

// ═══════════════════════════════════════════════════════════════════════════════
// LEAN-04 — UNIFIED LOGIN + ROLE ROUTING (replaces the v14.6 launcher)
// One login for everyone; the server returns the role; we route to the matching
// portal and hand it { token, user, session } so it renders pre-authenticated.
//
// Phase 2: admin super-user. An admin can "view as" a specific clinic or
// anaesthetist — we mount that portal with the admin's token plus an augmented
// `user` (so the UI shows the tenant), and activate viewAs() so every /api call
// carries X-Sykes-View-As and the server scopes the data to that one tenant.
// ═══════════════════════════════════════════════════════════════════════════════

const LazyAdminPortal = lazy(() => import('./admin/AdminPortal.jsx'));
const LazyAnaesthetistPortal = lazy(() => import('./anaesthetist/AnaesthetistPortal.jsx'));
const LazyClinicPortal = lazy(() => import('./clinic/ClinicPortal.jsx'));

const PORTAL_BY_ROLE = {
  admin: LazyAdminPortal,
  anaesthetist: LazyAnaesthetistPortal,
  clinic: LazyClinicPortal,
};

// Catches render crashes in a portal so the whole app doesn't white-screen, and
// surfaces the error on-screen instead (with a way back).
class PortalErrorBoundary extends React.Component {
  constructor(p) { super(p); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err, info) { console.error('[Portal crash]', err, info); }
  render() {
    if (this.state.err) {
      return (
        <div style={{ padding: 30, fontFamily: 'ui-monospace,monospace', color: '#7f1d1d', background: '#fef2f2', minHeight: '100vh' }}>
          <h2 style={{ margin: '0 0 8px' }}>⚠ Portal crashed</h2>
          <div style={{ fontSize: 13, marginBottom: 12 }}>{String(this.state.err && this.state.err.message || this.state.err)}</div>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: 11, background: '#fff', padding: 12, borderRadius: 8, border: '1px solid #fecaca', maxHeight: 360, overflow: 'auto' }}>{String(this.state.err && this.state.err.stack || '')}</pre>
          <button onClick={() => { this.setState({ err: null }); this.props.onReset && this.props.onReset(); }} style={{ marginTop: 14, padding: '8px 16px', borderRadius: 8, border: 'none', background: '#0f172a', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>← Back</button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function SykesApp() {
  // auth = { token, user, session } once logged in, else null.
  // LEAN-04-FU2: rehydrate synchronously from a short-lived /screener/ return ticket
  // (if any) so coming back from the Intelligence Portal skips the login screen.
  const [auth, setAuth] = useState(consumeReturnTicket);
  // superView = { portal:'clinic'|'anaesthetist', sel, label, tenantUser } | null
  const [superView, setSuperView] = useState(null);

  // APIStore refreshes the JWT in the background; keep our copy in sync so the
  // portal (which reads bootstrap.token) and audit pushes use the live token.
  useEffect(() => {
    setTokenRefreshCallback((newToken) => {
      setAuth((a) => (a ? { ...a, token: newToken } : a));
    });
  }, []);

  // LEAN-04-FU2: if we rehydrated from a return ticket above, re-run the per-session
  // setup UnifiedLogin normally does (we bypassed it) — at-rest encryption keyed the
  // same way, and the audit token. Mount-only: `auth` here is the initial value.
  useEffect(() => {
    if (auth?.token) {
      try { Encryption.init((auth.user?.username || auth.user?.role || '') + ':' + auth.token.slice(0, 16)); } catch {}
      AuditLogger.setAuthToken(auth.token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => { clearViewAs(); setSuperView(null); setAuth(null); };

  // Admin enters a tenant view. `tenantUser` overrides the display identity the
  // portal renders (clinic name / anaesthetist name+id); the token stays admin's.
  const enterTenant = ({ portal, sel, label, tenantUser }) => {
    setViewAs(sel);                       // synchronous: header is live before the portal mounts/loads
    AuditLogger.log('view_as_tenant', 'admin', auth?.user?.username || 'admin', `Admin viewing ${sel} (${label})`);
    setSuperView({ portal, sel, label, tenantUser });
  };
  const exitTenant = () => {
    AuditLogger.log('view_as_exit', 'admin', auth?.user?.username || 'admin', `Admin left ${superView?.sel || ''}`);
    clearViewAs();
    setSuperView(null);
  };

  if (!auth) {
    return <>
      <CookieBanner />
      <ResponsiveStyles />
      <UnifiedLogin onSuccess={setAuth} />
    </>;
  }

  // ── Admin super-user is viewing a tenant ──────────────────────────────────
  if (superView && auth.user.role === 'admin') {
    const Portal = PORTAL_BY_ROLE[superView.portal];
    // Rebuild bootstrap each render so background token refresh flows through.
    const tenantBootstrap = { ...auth, user: { ...auth.user, ...superView.tenantUser } };
    return (
      <Suspense fallback={<PortalLoader />}>
        <ViewAsBanner label={superView.label} portal={superView.portal} onExit={exitTenant} />
        <div style={{ paddingTop: 34 }}>
          <PortalErrorBoundary onReset={exitTenant}>
            <Portal bootstrap={tenantBootstrap} onLogout={logout} />
          </PortalErrorBoundary>
        </div>
      </Suspense>
    );
  }

  const Portal = PORTAL_BY_ROLE[auth.user.role];
  if (!Portal) {
    // Should never happen — UnifiedLogin already gates on known roles.
    return <div style={{ padding: 40, fontFamily: 'sans-serif' }}>No portal for role “{auth.user.role}”. <button onClick={logout}>Sign out</button></div>;
  }

  return (
    <Suspense fallback={<PortalLoader />}>
      <PortalErrorBoundary onReset={logout}>
        <Portal bootstrap={auth} onLogout={logout} onEnterTenant={auth.user.role === 'admin' ? enterTenant : undefined} />
      </PortalErrorBoundary>
    </Suspense>
  );
}

function ViewAsBanner({ label, portal, onExit }) {
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 10000, background: 'linear-gradient(135deg,#b45309,#d97706)', color: '#fff', padding: '7px 16px', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, boxShadow: '0 2px 8px rgba(0,0,0,.25)' }}>
      <span>👁 Admin view — {portal === 'clinic' ? 'Clinic' : 'Anaesthetist'}: <b>{label}</b> (read scope limited to this tenant)</span>
      <button onClick={onExit} style={{ padding: '3px 12px', borderRadius: 6, border: '1px solid rgba(255,255,255,.6)', background: 'rgba(255,255,255,.15)', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>Exit to Admin</button>
    </div>
  );
}
